import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import { closeTopModal, hasOpenModal, subscribeModalStack } from '../utils/modalStack';

// Debounce функция для оптимизации сохранения позиции скролла
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const useTelegramNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const isHomePage = location.pathname === '/' || location.pathname === '/home';

    // Управление BackButton. На главной кнопки нет — уходить некуда, но открытая
    // панель её показывает: иначе на главной «Назад» не закроет модалку
    const syncBackButton = (openModals) => {
      if (isHomePage && openModals === 0) {
        WebApp.BackButton.hide();
      } else {
        WebApp.BackButton.show();
      }
    };

    const unsubscribeModals = subscribeModalStack(syncBackButton);
    syncBackButton(hasOpenModal() ? 1 : 0);

    // Обработчик клика на BackButton
    const handleBackClick = () => {
      // Открытая панель перехватывает «Назад»: пользователь ждёт возврата
      // к месту, а не ухода с экрана. Брендбук 7.4
      if (closeTopModal()) return;

      // Сохраняем текущую позицию перед навигацией
      sessionStorage.setItem(`scroll_${location.pathname}`, window.scrollY.toString());
      navigate(-1, { state: { fromBack: true } });
    };

    WebApp.BackButton.onClick(handleBackClick);

    // Восстановление позиции скролла
    const savedPosition = sessionStorage.getItem(`scroll_${location.pathname}`);
    const savedLocationKey = sessionStorage.getItem(`locationKey_${location.pathname}`);

    // Определяем возврат назад: если сохраненный key совпадает с текущим
    const fromBack = savedLocationKey === location.key;

    if (savedPosition && fromBack) {
      // Восстанавливаем позицию при возврате назад
      const scrollPosition = parseInt(savedPosition, 10);
      let restored = false;

      // Используем MutationObserver для отслеживания изменений DOM
      const observer = new MutationObserver(() => {
        if (restored) return;

        const currentHeight = document.documentElement.scrollHeight;
        const targetHeight = scrollPosition + window.innerHeight;

        // Если контент загрузился достаточно
        if (currentHeight >= targetHeight) {
          window.scrollTo(0, scrollPosition);
          restored = true;
          observer.disconnect();
        }
      });

      // Наблюдаем за изменениями в body
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Дополнительная попытка через таймаут на случай если контент уже загружен
      setTimeout(() => {
        if (!restored) {
          const currentHeight = document.documentElement.scrollHeight;
          const targetHeight = scrollPosition + window.innerHeight;

          if (currentHeight >= targetHeight) {
            window.scrollTo(0, scrollPosition);
            restored = true;
            observer.disconnect();
          }
        }
      }, 100);

      // Отключаем observer через 3 секунды на всякий случай
      setTimeout(() => {
        if (!restored) {
          window.scrollTo(0, scrollPosition);
        }
        observer.disconnect();
      }, 3000);
    } else {
      // Скроллим в начало при новом переходе
      window.scrollTo(0, 0);
    }

    // Сохраняем location.key для определения возврата назад
    sessionStorage.setItem(`locationKey_${location.pathname}`, location.key);

    // Debounced обработчик для сохранения позиции скролла
    const saveScrollPosition = debounce(() => {
      sessionStorage.setItem(`scroll_${location.pathname}`, window.scrollY.toString());
    }, 150);

    window.addEventListener('scroll', saveScrollPosition);

    // Cleanup
    return () => {
      WebApp.BackButton.offClick(handleBackClick);
      window.removeEventListener('scroll', saveScrollPosition);
      unsubscribeModals();
    };
  }, [location, navigate]);
};

export default useTelegramNavigation;
